import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import queryString from 'query-string';

import { REACT_APP_API_URL, REACT_APP_SITE_KEY } from '../../shared/vars';

import getCartHeader from '../utils/cartHeader';
import getShippingCountry from '../utils/getShippingCountry';
import getShippingProvince from '../utils/getShippingProvince';
import getDeliveryId from '../utils/getDeliveryId';

import { clearShippingPaymentOption } from './shippingPayment.js';

// ===================================
//  Get cart
// ===================================
export const setCart = (payload) => {
    return {
        type: 'SET_CART',
        payload,
    };
};

export const getCart = (removeShipping = false) => {
    return (dispatch, getState) => {
        const header = getCartHeader(getState(), REACT_APP_SITE_KEY);
        const AUTH_HEADER = { Authorization: header };

        const shipping_country = getShippingCountry(getState());
        const shipping_province = getShippingProvince(getState());
        const delivery_id = getDeliveryId(getState());

        const args = { shipping_country, shipping_province, delivery_id };

        const qs = queryString.stringify(args);

        return axios
            .get(REACT_APP_API_URL + 'cart/?' + qs, { headers: AUTH_HEADER })
            .then((response) => {
                // Error explicitly set
                if (!!response.data.errors && !isEmpty(response.data.errors)) {
                    return Promise.reject(response.data.errors);

                    // Error status
                } else if (!!response.data.status && response.data.status !== 200) {
                    return Promise.reject([response.data.message]);

                    // Success
                } else {
                    if (removeShipping) response.data.shipping_method = {};
                    dispatch(setCart(response.data));
                    return Promise.resolve(response.data);
                }
            })
            .catch((err) => {
                console.log('getCart error', err);
                return Promise.reject(err);
            });
    };
};

// ===================================
//	Add to cart
// ===================================
export const addToCartInProcess = () => {
    return {
        type: 'ADD_CART_IN_PROCESS',
    };
};

export const addToCartSuccess = (payload) => {
    return {
        type: 'ADD_CART_SUCCESS',
        payload,
    };
};

export const addToCartError = (error) => {
    return {
        type: 'ADD_CART_ERROR',
        error,
    };
};

export function addToCart(product_id, quantity, variants) {
    return (dispatch, getState) => {
        dispatch(addToCartInProcess());

        const header = getCartHeader(getState(), REACT_APP_SITE_KEY);
        const AUTH_HEADER = { Authorization: header };

        const shipping_country = getShippingCountry(getState());
        const shipping_province = getShippingProvince(getState());
        const delivery_id = getDeliveryId(getState());

        // Set up request params
        const params = {
            product: product_id,
            qty: quantity,
            shipping_country: shipping_country,
            shipping_province: shipping_province,
            delivery_id: delivery_id,
        };

        // Serialize the variant params
        if (variants.length) {
            params.variants = {};
        }

        for (let key in variants) {
            if (!variants.hasOwnProperty(key)) continue;
            params.variants[variants[key].variant_id] = variants[key].option_id;
        }

        return axios
            .post(REACT_APP_API_URL + 'cart/', params, { headers: AUTH_HEADER })
            .then((response) => {
                // console.log(response)
                // Error explicitly set
                if (!isEmpty(response.data.errors)) {
                    dispatch(addToCartError(response.data.errors));

                    // Error status
                } else if (response.data.status !== 200) {
                    dispatch(addToCartError([response.data.message]));

                    // Success
                } else {
                    dispatch(addToCartSuccess(response.data));
                }
            })
            .catch((err) => {
                console.log('addToCart error', err);
                dispatch(addToCartError(err));
            });
    };
}

// ===================================
//	Update cart quantity
// ===================================
export const updateCartProductSuccess = (payload, item_id) => {
    return {
        type: 'UPDATE_CART_QTY_SUCCESS',
        payload,
        item_id,
    };
};

export const updateCartProductInProcess = (item_id) => {
    return {
        type: 'UPDATE_CART_QTY_IN_PROCESS',
        item_id,
    };
};

export const updateCartProductError = (error, item_id) => {
    return {
        type: 'UPDATE_CART_QTY_ERROR',
        error,
        item_id,
    };
};

export const clearUpdateMsg = () => {
    return {
        type: 'CLEAR_UPDATE_MESSAGES',
    };
};

export function updateCartQty(item_id, quantity) {
    return (dispatch, getState) => {
        dispatch(updateCartProductInProcess(item_id));

        const header = getCartHeader(getState(), REACT_APP_SITE_KEY);
        const AUTH_HEADER = { Authorization: header };

        const shipping_country = getShippingCountry(getState());
        const shipping_province = getShippingProvince(getState());
        const delivery_id = getDeliveryId(getState());

        // Set up request params
        const params = {
            qty: quantity,
            shipping_country: shipping_country,
            shipping_province: shipping_province,
            delivery_id: delivery_id,
        };

        return axios
            .post(REACT_APP_API_URL + 'cart/update/' + item_id, params, {
                headers: AUTH_HEADER,
            })
            .then((response) => {
                // Error explicitly set
                if (!isEmpty(response.data.errors)) {
                    dispatch(updateCartProductError(response.data.errors, item_id));

                    // Error status
                } else if (response.data.status !== 200) {
                    dispatch(updateCartProductError([response.data.message], item_id));

                    // Success
                } else {
                    dispatch(updateCartProductSuccess(response.data, item_id));
                }
            })
            .catch((err) => {
                console.log('updateCartQty error', err);
                dispatch(updateCartProductError(err, item_id));
            });
    };
}

// ===================================
//	Remove from cart
// ===================================
export const removeCartProductSuccess = (payload, item_id) => {
    return {
        type: 'REMOVE_FROM_CART_SUCCESS',
        payload,
        item_id,
    };
};

export const removeCartProductInProcess = (item_id) => {
    return {
        type: 'REMOVE_FROM_CART_IN_PROCESS',
        item_id,
    };
};

export const removeCartProductError = (error, item_id) => {
    return {
        type: 'REMOVE_FROM_CART_ERROR',
        error,
        item_id,
    };
};

export function removeCartProduct(item_id) {
    return (dispatch, getState) => {
        dispatch(removeCartProductInProcess(item_id));

        const header = getCartHeader(getState(), REACT_APP_SITE_KEY);
        const AUTH_HEADER = { Authorization: header };

        const shipping_country = getShippingCountry(getState());
        const shipping_province = getShippingProvince(getState());
        const delivery_id = getDeliveryId(getState());

        const params = {
            shipping_country: shipping_country,
            shipping_province: shipping_province,
            delivery_id: delivery_id,
        };

        // DELETE requests require the params to be set in config.  They end up as query params eg: someurl?cow=pie
        const config = {
            headers: AUTH_HEADER,
            params: params,
        };

        return axios
            .delete(REACT_APP_API_URL + 'cart/update/' + item_id, config)
            .then((response) => {
                // Error explicitly set
                if (!isEmpty(response.data.errors)) {
                    // console.log(response.data.errors);
                    dispatch(removeCartProductError(response.data.errors, item_id));

                    // Error status
                } else if (response.data.status !== 200) {
                    // console.log(response);
                    dispatch(removeCartProductError(response.data.message, item_id));

                    // Success
                } else {
                    dispatch(removeCartProductSuccess(response.data, item_id));
                }
            })
            .catch((err) => {
                console.log('removeCartProduct error', err);
                dispatch(removeCartProductError(err.response.data.message, item_id));
            });
    };
}

// ===================================
//  Add coupon
// ===================================
export const addCouponToCartInProcess = () => {
    return {
        type: 'ADD_COUPON_CART_IN_PROCESS',
    };
};

export const addCouponToCartSuccess = (payload) => {
    return {
        type: 'ADD_COUPON_CART_SUCCESS',
        payload,
    };
};

export const addCouponToCartError = (error) => {
    return {
        type: 'ADD_COUPON_CART_ERROR',
        error,
    };
};

// ===================================
//	Remove coupon
// ===================================
export const removeCartCouponSuccess = (payload, item_id) => {
    return {
        type: 'REMOVE_COUPON_FROM_CART_SUCCESS',
        payload,
        item_id,
    };
};

export const removeCartCouponInProcess = (item_id) => {
    return {
        type: 'REMOVE_COUPON_FROM_CART_IN_PROCESS',
        item_id,
    };
};

export const removeCartCouponError = (error, item_id) => {
    return {
        type: 'REMOVE_COUPON_FROM_CART_ERROR',
        error,
        item_id,
    };
};

// ===================================
//  Clear messages
// ===================================
export const clearDeleteMsg = () => {
    return {
        type: 'CLEAR_DELETE_MESSAGES',
    };
};

// ===================================
//	Clear
// ===================================
export const clearSuccess = () => {
    return {
        type: 'CLEAR_SUCCESS',
    };
};

export function clearCart() {
    return {
        type: 'CLEAR_CART',
    };
}

// ===================================================================================
//  COUPONS
// ===================================================================================
export function addCouponToCart(code) {
    return (dispatch, getState) => {
        dispatch(addCouponToCartInProcess());

        const header = getCartHeader(getState(), REACT_APP_SITE_KEY);
        const AUTH_HEADER = { Authorization: header };

        const shipping_country = getShippingCountry(getState());
        const shipping_province = getShippingProvince(getState());
        const delivery_id = getDeliveryId(getState());

        // Set up request params
        const params = {
            code: code,
            shipping_country: shipping_country,
            shipping_province: shipping_province,
            delivery_id: delivery_id,
        };

        return axios
            .post(REACT_APP_API_URL + 'cart-code/?apply=true', params, {
                headers: AUTH_HEADER,
            })
            .then((response) => {
                // Error explicitly set
                if (!isEmpty(response.data.errors)) {
                    dispatch(addCouponToCartError(response.data.errors));

                    // Error status
                } else if (response.data.status !== 200) {
                    dispatch(addCouponToCartError([response.data.message]));

                    // Success
                } else {
                    dispatch(addCouponToCartSuccess(response.data));
                }
            })
            .catch((err) => {
                console.log('addCouponToCart error', err);
                dispatch(addCouponToCartError(err));
            });
    };
}

export function addDiscountToCart(code, codeData) {
    return (dispatch, getState) => {
        dispatch(addCouponToCartInProcess());

        const header = getCartHeader(getState(), REACT_APP_SITE_KEY);
        const AUTH_HEADER = { Authorization: header };

        const shipping_country = getShippingCountry(getState());
        const shipping_province = getShippingProvince(getState());
        const delivery_id = getDeliveryId(getState());

        // Set up request params
        const params = {
            code: code,
            shipping_country: shipping_country,
            shipping_province: shipping_province,
            delivery_id: delivery_id,
        };

        return axios
            .post(REACT_APP_API_URL + 'discount-multiple/?apply=true', params, {
                headers: AUTH_HEADER,
            })
            .then((response) => {
                // Error explicitly set
                if (!isEmpty(response.data.errors)) {
                    dispatch(addCouponToCartError(response.data.errors));

                    // Error status
                } else if (response.data.status !== 200) {
                    dispatch(addCouponToCartError([response.data.message]));

                    // Success
                } else {
                    dispatch(addCouponToCartSuccess(response.data));

                    // If discount code gives free shipping, remove payment option
                    if (!!codeData && codeData.give_free_shipping === 1) {
                        console.log('running');
                        dispatch(clearShippingPaymentOption());
                    }
                }
            })
            .catch((err) => {
                console.log('addCouponToCart error', err);
                dispatch(addCouponToCartError(err));
            });
    };
}

export function removeCartCoupon(item_id) {
    return (dispatch, getState) => {
        dispatch(removeCartCouponInProcess(item_id));

        const header = getCartHeader(getState(), REACT_APP_SITE_KEY);
        const AUTH_HEADER = { Authorization: header };

        const shipping_country = getShippingCountry(getState());
        const shipping_province = getShippingProvince(getState());
        const delivery_id = getDeliveryId(getState());

        const params = {
            shipping_country: shipping_country,
            shipping_province: shipping_province,
            delivery_id: delivery_id,
        };

        // DELETE requests require the params to be set in config.  They end up as query params eg: someurl?cow=pie
        const config = {
            headers: AUTH_HEADER,
            params: params,
        };

        return axios
            .delete(REACT_APP_API_URL + 'cart-code/code/' + item_id, config)
            .then((response) => {
                // Error explicitly set
                if (!isEmpty(response.data.errors)) {
                    // console.log(response.data.errors)
                    dispatch(removeCartCouponError(response.data.errors, item_id));

                    // Error status
                } else if (response.data.status !== 200) {
                    // console.log(response)
                    dispatch(removeCartCouponError(response.data.message, item_id));

                    // Success
                } else {
                    dispatch(removeCartCouponSuccess(response.data, item_id));
                }
            })
            .catch((err) => {
                console.log('removeCartCoupon error', err);
                dispatch(removeCartCouponError(err.response.data.message, item_id));
            });
    };
}

// ===================================================================================
//	BASIC CART UPDATE
// ===================================================================================
export const updateCart = (payload) => {
    return {
        type: 'UPDATE_CART',
        payload,
    };
};



// WEBPACK FOOTER //
// ./src/store/actions/cart.js